import { useDraggable, useDroppable } from '@dnd-kit/core';
import clsx from 'clsx';
import { useId, useRef, useState } from 'react';
import type { RefObject, ToggleEvent } from 'react';
import { FiPlus } from 'react-icons/fi';
import { TbAnchor, TbCrown, TbUserOff } from 'react-icons/tb';
import { toast } from 'sonner';

import type { ApiException } from '@/common/error';
import { Avatar } from '@/components';
import { CountChip, Row } from '@/design-system/Layout';
import { useRemoveSeatMutation } from '@/store/people/people.store';

import s from '../subscription.module.scss';
import { SEAT_POOL_LABEL, canVacate, displayName } from '../subscription.seats';
import type { SeatPerson, SeatPool } from '../subscription.seats';
import type { SeatPoolState } from '../useVesselSeats';

import { AssignSeatModal } from './AssignSeatModal';
import { SeatPoolIcon } from './Chips';

export const SEAT_AVATAR_SIZE = 30;

/** What a dragged face carries: who it is, and the pool they are leaving — `null` for no seat. */
export interface SeatDrag {
  person: SeatPerson;
  pool: SeatPool | null;
}

/** A pool a seated person could be moved into, and what a full one takes to get in. */
export interface SeatTarget {
  state: SeatPoolState;
  /** `null` while the pool has room. */
  offer: 'buy' | 'upgrade' | null;
}

/** How full the pool is — a full one is still offered when a seat can be bought or upgraded. */
const targetNote = ({ state, offer }: SeatTarget) => {
  if (!offer) return `${state.capacity - state.occupants.length} open`;
  return offer === 'upgrade' ? 'Full · upgrade plan' : 'Full · add a seat';
};

/**
 * As with assigning, the causes share one error `type` and the body carries no message, so
 * the copy names the two that can still happen once the action has been offered: the seat
 * was already freed elsewhere, or the person's role changed under a stale list.
 */
const emptyError = (error: unknown, name: string) =>
  (error as ApiException | null)?.type === 'NOT_FOUND'
    ? `${name} is no longer on this vessel.`
    : `Could not empty ${name}'s seat. It may already be free, or their role changed — reload to see where things stand.`;

/**
 * Opens the menu under its face, or over it when the face sits low on the screen. Only the
 * anchor is set here — the menu's width, and the clamp that keeps it on screen, are in CSS.
 */
const placeMenu = (event: ToggleEvent<HTMLDivElement>, anchor: HTMLElement | null) => {
  if (event.newState !== 'open' || !anchor) return;
  const { left, top, bottom } = anchor.getBoundingClientRect();
  const opensUp = bottom > window.innerHeight * 0.6;
  const menu = event.currentTarget.style;
  menu.setProperty('--anchor-left', `${left}px`);
  menu.top = opensUp ? 'auto' : `${bottom + 8}px`;
  menu.bottom = opensUp ? `${window.innerHeight - top + 8}px` : 'auto';
};

interface SeatMenuProps {
  person: SeatPerson;
  /** The pool they sit in; `null` when they hold no seat, so there is none to empty. */
  pool: SeatPool | null;
  serviceId: string;
  /** The pools they could be moved or given into — the same ones a drag would land in. */
  targets: SeatTarget[];
  onMove: (to: SeatPool) => void;
  /** Why there is nothing to do with them — shown in place of the actions. */
  note?: string;
}

/**
 * A face's seat options: move them to another pool (give them one, when they have none), or
 * empty the seat they hold. A native popover: it sits in the top layer, clear of the group's
 * overflow and backdrop-filter, and closes on Escape or a click elsewhere. It does not follow
 * the face when the page scrolls.
 */
function SeatMenu({
  id,
  anchor,
  person,
  pool,
  serviceId,
  targets,
  onMove,
  note,
}: SeatMenuProps & {
  id: string;
  /** The face it opens from, measured on each open to place the menu. */
  anchor: RefObject<HTMLButtonElement | null>;
}) {
  const menu = useRef<HTMLDivElement>(null);
  const remove = useRemoveSeatMutation();
  const name = displayName(person);

  const move = (to: SeatPool) => {
    menu.current?.hidePopover();
    onMove(to);
  };

  const empty = () =>
    remove.mutate(
      { personId: person.personId, serviceId },
      {
        // The face stays until the refetch lands, so the menu is closed rather than left open on it.
        onSuccess: () => menu.current?.hidePopover(),
        onError: (error) => toast.error(emptyError(error, name)),
      },
    );

  return (
    <div
      ref={menu}
      id={id}
      popover="auto"
      className={s.seatMenu}
      onBeforeToggle={(event) => placeMenu(event, anchor.current)}
    >
      <div className={s.seatMenuName}>{name}</div>
      {note && <p className={s.seatMenuText}>{note}</p>}

      {targets.length > 0 && (
        <div className={s.seatMenuGroup}>
          {targets.map((target) => (
            <button
              key={target.state.pool}
              type="button"
              className={s.seatMenuItem}
              onClick={() => move(target.state.pool)}
            >
              {pool ? 'Move to' : 'Give'} {SEAT_POOL_LABEL[target.state.pool]} seat
              <span className={target.offer ? s.seatMenuNoteFull : s.seatMenuNote}>
                {targetNote(target)}
              </span>
            </button>
          ))}
        </div>
      )}

      {pool && (
        <div className={s.seatMenuGroup}>
          <button
            type="button"
            className={s.seatMenuEmpty}
            onClick={empty}
            disabled={remove.isPending}
          >
            {remove.isPending ? 'Emptying…' : 'Empty seat'}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * One occupied seat, or one person holding none. Titled so a face alone is still identifiable,
 * and a button when the seat can be changed — clicking the person opens their `SeatMenu`,
 * dragging them onto a pool does the same move. Both are the same right, so one gate covers them.
 */
function SeatAvatar({
  editable,
  ...menuProps
}: SeatMenuProps & {
  /** Whether the viewer may change this seat; when not, the face is just a picture. */
  editable: boolean;
}) {
  const { person, pool, note } = menuProps;
  const menuId = useId();
  const anchor = useRef<HTMLButtonElement | null>(null);
  const { setNodeRef, listeners, isDragging } = useDraggable({
    id: person.personId,
    data: { person, pool } satisfies SeatDrag,
    disabled: !editable,
  });

  const name = displayName(person);
  const hint = !editable
    ? `${name}: ${note}`
    : pool
      ? `${name}: click for seat options, or drag to move`
      : `${name}: click or drag onto a pool to give a seat`;
  const face = (
    <Avatar
      src={person.avatar}
      firstName={person.firstName}
      lastName={person.lastName}
      size={SEAT_AVATAR_SIZE}
      alt={name}
    />
  );

  // A face with a note still opens its menu, to say why — it just cannot be dragged.
  if (!editable && !note)
    return (
      <div className={s.seat} title={name}>
        {face}
      </div>
    );

  // ponytail: dnd-kit's `attributes` are left off — they announce a keyboard drag that is not
  // wired. Keyboard users move a seat from the menu instead.
  return (
    <>
      <button
        ref={(node) => {
          setNodeRef(node);
          anchor.current = node;
        }}
        {...listeners}
        type="button"
        className={clsx(s.seatOccupant, isDragging && s.seatDragging)}
        popoverTarget={menuId}
        title={hint}
        aria-label={hint}
      >
        {face}
      </button>
      <SeatMenu id={menuId} anchor={anchor} {...menuProps} />
    </>
  );
}

/**
 * An empty seat. Rendered as a dashed outline whether or not it can be filled, so the
 * paid-for-but-empty seats are visible even to someone who cannot assign them.
 */
function EmptySeat({
  onClick,
  label,
  landing,
}: {
  onClick?: () => void;
  label?: string;
  /** Where a face dragged into this pool would sit. */
  landing?: boolean;
}) {
  if (!onClick) return <div className={clsx(s.seatEmpty, landing && s.seatLanding)} aria-hidden />;

  return (
    <button
      type="button"
      className={clsx(s.seatAdd, landing && s.seatLanding)}
      onClick={onClick}
      aria-label={label}
    >
      <FiPlus />
    </button>
  );
}

/**
 * The management seat kept for an owner who has not joined yet. Paid for and empty like the
 * slots beside it, but never offered — that is what holding it means — so it is marked rather
 * than left looking like a seat nobody got round to filling.
 */
function HeldSeat() {
  const label = 'Held for the owner you are adding';

  return (
    <div className={s.seatHeld} title={label} aria-label={label}>
      <TbCrown />
    </div>
  );
}

/**
 * A seat pool as faces rather than a bar: one avatar per occupied seat, one dashed slot per
 * free one, and the count alongside. The first free slot is the add button when there is
 * anyone eligible to put in it and the viewer may assign seats.
 *
 * Only the first empty seat is clickable — the rest are there to show what is paid for. An
 * occupied seat is clickable when it can be emptied, which opens its menu: move or free it.
 */
export function SeatRow({
  state,
  vesselName,
  serviceId,
  accepts,
  moveTargets,
  onMove,
}: {
  state: SeatPoolState;
  vesselName: string;
  serviceId: string;
  /** Whether the face being dragged right now may be dropped into this pool. */
  accepts: boolean;
  /** Where a face in this pool could be moved — the pools a drag of it would land in. */
  moveTargets: (drag: SeatDrag) => SeatTarget[];
  onMove: (drag: SeatDrag, to: SeatPool) => void;
}) {
  const [isAssignOpen, setIsAssignOpen] = useState(false);

  const { pool, capacity, occupants, heldForOwner, canAdd, canManage, viewerPersonId } = state;
  const { setNodeRef, isOver } = useDroppable({ id: pool, disabled: !accepts });
  const label = SEAT_POOL_LABEL[pool];
  // A seat held for an owner still to come is neither occupied nor open, so it is taken off
  // both counts and drawn on its own.
  const free = Math.max(0, capacity - occupants.length - heldForOwner);

  const sub = () => {
    if (capacity === 0) return 'No seats on this plan';
    const held = heldForOwner > 0 ? '1 held for the owner' : null;
    const open = free === 0 ? 'all others in use' : `${free} ${free === 1 ? 'seat' : 'seats'} open`;
    if (held) return `${held} · ${open}`;
    return free === 0 ? 'All seats in use' : open;
  };

  return (
    <>
      <Row
        ref={setNodeRef}
        // Amber over a full pool: dropping there buys a seat rather than takes a free one.
        className={isOver ? (free > 0 ? s.seatDropOver : s.seatDropFull) : undefined}
        icon={<SeatPoolIcon pool={pool} />}
        title={label}
        sub={sub()}
        trailing={<CountChip>{`${occupants.length}/${capacity}`}</CountChip>}
      >
        {capacity > 0 && (
          <div className={s.seats}>
            {occupants.map((person) => {
              const editable = canManage && canVacate(person, viewerPersonId);
              return (
                <SeatAvatar
                  key={person.personId}
                  person={person}
                  pool={pool}
                  serviceId={serviceId}
                  editable={editable}
                  targets={editable ? moveTargets({ person, pool }) : []}
                  onMove={(to) => onMove({ person, pool }, to)}
                />
              );
            })}
            {Array.from({ length: heldForOwner }, (_, index) => (
              <HeldSeat key={`held-${index}`} />
            ))}
            {Array.from({ length: free }, (_, index) => (
              <EmptySeat
                key={`empty-${index}`}
                landing={accepts && index === 0}
                onClick={canAdd && index === 0 ? () => setIsAssignOpen(true) : undefined}
                label={`Assign a ${label.toLowerCase()} seat on ${vesselName}`}
              />
            ))}
          </div>
        )}
      </Row>

      {isAssignOpen && (
        <AssignSeatModal
          open
          state={state}
          vesselName={vesselName}
          serviceId={serviceId}
          onClose={() => setIsAssignOpen(false)}
        />
      )}
    </>
  );
}

/**
 * Everyone on the vessel who could hold a seat but holds none, as faces like the pools above. A
 * face is a button when some pool will take them — its menu, or a drag, gives them a seat. One no
 * pool will take — every one they may join full, with no seat to buy on a plan behind on payment
 * or ending — still opens a menu saying so, as long as the viewer could give seats at all. Left
 * out when there is no one.
 */
export function NoSeatRow({
  people,
  serviceId,
  canManage,
  moveTargets,
  onMove,
}: {
  people: SeatPerson[];
  serviceId: string;
  /** Whether the viewer may change any pool — when not, every face is just a picture. */
  canManage: boolean;
  moveTargets: (drag: SeatDrag) => SeatTarget[];
  onMove: (drag: SeatDrag, to: SeatPool) => void;
}) {
  if (people.length === 0) return null;

  // ponytail: not a drop target — dragging a face here to empty its seat is the menu's job.
  return (
    <Row
      icon={<TbUserOff />}
      title="No seat"
      sub={
        people.length === 1 ? '1 person without a seat' : `${people.length} people without a seat`
      }
    >
      <div className={s.seats}>
        {people.map((person) => {
          const targets = moveTargets({ person, pool: null });
          return (
            <SeatAvatar
              key={person.personId}
              person={person}
              pool={null}
              serviceId={serviceId}
              editable={targets.length > 0}
              targets={targets}
              onMove={(to) => onMove({ person, pool: null }, to)}
              note={
                canManage && targets.length === 0
                  ? 'Every seat they could take is full.'
                  : undefined
              }
            />
          );
        })}
      </div>
    </Row>
  );
}

/**
 * Guests, apart from everyone who could be seated: they stay on the free `harbor_seat` and are
 * never given a paid one, so their faces are pictures and the row says why once for all of them.
 */
export function GuestRow({ people }: { people: SeatPerson[] }) {
  if (people.length === 0) return null;

  return (
    <Row
      icon={<TbAnchor />}
      title="Guests"
      sub="On the free Harbor seat — guests can't hold a paid seat"
    >
      <div className={s.seats}>
        {people.map((person) => (
          <div key={person.personId} className={s.seat} title={displayName(person)}>
            <Avatar
              src={person.avatar}
              firstName={person.firstName}
              lastName={person.lastName}
              size={SEAT_AVATAR_SIZE}
              alt={displayName(person)}
            />
          </div>
        ))}
      </div>
    </Row>
  );
}
